import React, { useMemo, ElementType, FC, forwardRef, ReactNode, ForwardedRef } from 'react';
import { ButtonBase, ButtonBaseProps, Box, BoxProps, useTheme } from '@mui/material';
import { ArrowTopRightIcon } from '@/src/components/icon/stroke/ArrowTopRightIcon';
import { getFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';
import { getTypography } from '@/src/materialui/typography/typography.theme';

export interface HeaderNavigationLinkProps extends ButtonBaseProps<ElementType> {
  /**
   * Displayed navigation text, can be provided as children as well
   */
  label?: ReactNode;
  /**
   * Marks item currently active
   * @default false
   */
  active?: boolean;
  /**
   * Indicates external link
   * @default false
   */
  external?: boolean;
  /**
   * Expect icon component that will be rendered after the label
   */
  icon?: ReactNode;
  /**
   * Change link into extended mobile version
   */
  extend?: boolean;
  /**
   * Properties applied to wrapper of label
   */
  labelWrapperProps?: BoxProps<ElementType>;
  /**
   * Properties applied to default label
   */
  labelProps?: BoxProps<ElementType>;
  /**
   * Properties applied to selected label
   */
  selectedLabelProps?: BoxProps<ElementType>;
}

/**
 * Component to render styled button in the header
 */
export const HeaderNavigationLink: FC<HeaderNavigationLinkProps> = forwardRef(
  (
    {
      sx,
      children,
      label,
      icon,
      active = false,
      external = false,
      extend,
      labelWrapperProps,
      labelProps,
      selectedLabelProps,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const { sx: labelWrapperSx, ...restLabelWrapper } = labelWrapperProps ?? {};
    const { sx: labelSx, ...restLabel } = labelProps ?? {};
    const { sx: selectedLabelSx, ...restSelectedLabel } = selectedLabelProps ?? {};
    const linkIcon = useMemo(() => {
      return external ? <ArrowTopRightIcon /> : icon;
    }, [icon, external]);

    return (
      <ButtonBase
        sx={{
          ...theme.typography.list,
          ...(extend === undefined
            ? {
                [theme.breakpoints.down('lg')]: {
                  // switch to 'large body' typography according to Figma
                  ...getTypography('body', theme.direction === 'rtl', 'large'),
                },
              }
            : [
                {
                  '&&': {
                    ...getTypography('body', theme.direction === 'rtl', 'large'),
                  },
                },
                {},
              ][+!extend]),
          position: 'relative',
          zIndex: '100',
          // icon and text next to each other
          display: extend === undefined ? { xs: 'flex', lg: 'inline-flex' } : ['flex', 'inline-flex'][+!extend],
          width: extend === undefined ? { xs: '100%', lg: 'auto' } : ['100%', 'auto'][+!extend],
          gap: 0.5,
          alignItems: 'center',
          justifyContent: 'flex-start',
          py: extend === undefined ? { xs: 2, lg: 1 } : [2, 1][+!extend],
          px: extend === undefined ? { xs: 1, lg: 0.5 } : [1, 0.5][+!extend],
          transition:
            'transform 0.3s ease-out, color 0.3s ease-out, background-color 0.3s ease-out, border-color 0.3s ease-out, box-shadow 0.3s ease-out, opacity 0.3s ease-out',
          // size of icon
          '& .MuiSvgIcon-root': {
            fontSize: '14px',
          },
          // bottom border
          '&:after': {
            content: extend === undefined ? { xs: 'none', lg: '""' } : ['none', '""'][+!extend],
            position: 'absolute',
            left: '0',
            right: '0',
            // move directly into border of appbar
            bottom: '0',
            height: '1px',
            backgroundColor: 'transparent.transparent',
            ...(active && {
              height: '2px',
              backgroundColor: 'text.primary',
            }),
          },
          // disabled style
          '&.Mui-disabled': {
            color: 'disabled.tertiary',
          },
          // interactive style
          ...getInteractiveStyle(
            {
              color: theme.palette.mode === 'dark' ? 'onSurface.os300' : 'onSurface.os700',
              '&&:after': {
                backgroundColor: 'transparent.transparent',
              },
              '& .FlowHeaderNavigationLink-label': {
                opacity: '1',
              },
              '& .FlowHeaderNavigationLink-selectedLabel': {
                opacity: '0',
              },
              // active state is same as hover state
              ...(active && {
                color: 'text.primary',
                '&&:after': {
                  backgroundColor: 'text.primary',
                },
                '& .FlowHeaderNavigationLink-label': {
                  opacity: '0',
                },
                '& .FlowHeaderNavigationLink-selectedLabel': {
                  opacity: '1',
                },
              }),
            },
            {
              color: 'text.primary',
              '&&:after': {
                backgroundColor: 'text.primary',
              },
              '& .FlowHeaderNavigationLink-label': {
                opacity: '0',
              },
              '& .FlowHeaderNavigationLink-selectedLabel': {
                opacity: '1',
              },
            },
          ),
          ...getFocusRingStyle(theme),
          // adjust focus ring
          '&&:before': {
            borderRadius: '4px',
            // make sure top border is not cropped
            top: extend === undefined ? { xs: '0', lg: '2px' } : ['0', '2px'][+!extend],
            bottom: extend === undefined ? { xs: '0', lg: '-2px' } : ['0', '-2px'][+!extend],
          },
          ...sx,
        }}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        data-testid="HeaderNavigationLink"
        {...rest}
      >
        {(children || label) && (
          <Box
            className="FlowHeaderNavigationLink-labelWrapper"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              position: 'relative',
              ...labelWrapperSx,
            }}
            {...restLabelWrapper}
          >
            <Box
              className="FlowHeaderNavigationLink-label"
              sx={{
                position: 'absolute',
                left: extend === undefined ? { xs: '0', lg: 'auto' } : ['0', 'auto'][+!extend],
                ...labelSx,
              }}
              {...restLabel}
            >
              {children || label}
            </Box>
            <Box
              className="FlowHeaderNavigationLink-selectedLabel"
              sx={{
                fontWeight: '700',
                opacity: '0',
                position: 'relative',
                ...selectedLabelSx,
              }}
              aria-hidden="true"
              {...restSelectedLabel}
            >
              {children || label}
            </Box>
          </Box>
        )}
        {linkIcon}
      </ButtonBase>
    );
  },
);
